import React, { useCallback, useMemo } from 'react';
import Toggle from '@atlaskit/toggle';
import { withFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useResource } from '@atlassian/jira-router';
import { gicSmartFieldsFeatureResource } from '@atlassian/jira-router-resources-gic-smart-fields';
import { messages } from '../../common/messages';
import { useSmartFieldsFeedbackCollector } from '../../common/ui/feedback';
import { useSmartFieldsUpdateUserPreference } from '../../services';

export const useSmartFieldsPreferenceResource = () => useResource(gicSmartFieldsFeatureResource);

export const SmartFieldsPreference = withFlagService((props) => {
	const { loading, data, update: updateResource } = useSmartFieldsPreferenceResource();
	const { updatePreferences } = useSmartFieldsUpdateUserPreference();
	const { formatMessage } = useIntl();

	const {
		flagService: { showFlag },
	} = props;

	const hasUserOptedOut = useMemo(
		() => (typeof data?.hasUserOptedOut === 'boolean' ? data.hasUserOptedOut : false),
		[data],
	);

	const hasUserDismissedReadMore = useMemo(
		() =>
			typeof data?.hasUserDismissedReadMore === 'boolean' ? data.hasUserDismissedReadMore : false,
		[data],
	);
	const { openSmartFieldsFeedbackCollector } = useSmartFieldsFeedbackCollector();

	const onChange = useCallback(
		async (_: React.ChangeEvent<HTMLInputElement>, analyticsEventUI: UIAnalyticsEvent) => {
			const value = !hasUserOptedOut;
			updateResource(() => ({ hasUserOptedOut: value, hasUserDismissedReadMore: false }));

			fireUIAnalytics(analyticsEventUI, 'toggleLabsGicSmartFields', {
				isSmartFieldsOptOut: value,
			});

			try {
				await updatePreferences({
					hasUserOptedOut: value,
					hasUserDismissedReadMore: false,
				});
				showFlag({
					type: 'success',
					title: value ? messages.successTitleOptingOut : messages.successTitleOptingIn,
				});

				if (value) {
					openSmartFieldsFeedbackCollector({
						feedbackTitle: formatMessage(messages.feedbackOptOutTitle),
						feedbackContext: [
							{
								id: 'smart-fields-opt-out',
								value: 'opted-out',
							},
						],
					});
				}
			} catch (err) {
				updateResource(() => ({ hasUserOptedOut, hasUserDismissedReadMore }));
				const actions = [
					{
						content: messages.refreshPageBtn,
						onClick: () => {
							window.location.reload();
						},
					},
				];
				showFlag({
					type: 'error',
					title: value ? messages.errTitleOptingOut : messages.errTitleOptingIn,
					description: messages.errDescriptionOptingIn,
					actions,
				});
			}
		},
		[
			hasUserDismissedReadMore,
			hasUserOptedOut,
			updateResource,
			updatePreferences,
			showFlag,
			openSmartFieldsFeedbackCollector,
			formatMessage,
		],
	);

	if (loading) return null;

	return <Toggle isChecked={!hasUserOptedOut} onChange={onChange} />;
});
